import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, Col, Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';

import {
  AlertFormSteps,
  clearStepData,
  loadAlertForEdit,
  resetForm,
  restoreFormState,
  selectAlertFormIsValid,
  selectAlertFormState,
  selectEditAlertIsLoaded,
  selectEditAlertLoadStatus,
  selectSensorsData,
  setAlertFormMode,
  updateAlert,
} from '../../../redux/slices/alertFormSlice/alertFormSlice';

import { fetchFilteredAlertList } from '../../../redux/slices/alertListSlice/alertListSlice';

import AlertFormReviewLayout from './AlertFormReview/AlertFormReview';
import { STEP_ORDER } from './CreateAlertLayout';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import { clearAppliedChannelSearch } from '../../../redux/slices/channelListSlice/channelListSlice';

import {
  validateDefineActionSection,
  validateDefineThresholdSection,
  validateNameAlertAndMoreSection,
  validateSelectSensorsSection,
} from './AlertFormValidation';

/* STEP FORMS */
import SelectSensorsForm from './SelectSensorsSection/SelectSensorsForm';
import DefineThresholdsForm from './DefineThresholdSection/DefineThresholdsForm';
import DefineActionsForm from './DefineActionsSection/DefineActionsForm';
import NameAlertAndMoreForm from './NameAlertAndMoreSection/NameAlertAndMoreForm';

/* STEP SUMMARIES */
import SelectSensorsSummary from './SelectSensorsSection/SelectSensorsSummary';
import DefineThresholdSummary from './DefineThresholdSection/DefineThresholdsSummary';
import DefineActionsSummary from './DefineActionsSection/DefineActionsSummary';
import NameAlertAndMoreSummary from './NameAlertAndMoreSection/NameAlertAndMoreSummary';
import { getIncompleteSteps } from './AlertFormUtility';

const EditAlertFormWrapper = () => {
  const history = useHistory();
  const { alertId } = useParams();
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(STEP_ORDER[4]);
  const [editingStep, setEditingStep] = useState(null);

  const isEditLoaded = useSelector(selectEditAlertIsLoaded);
  const editLoadStatus = useSelector(selectEditAlertLoadStatus);
  const canGoNext = useSelector(selectAlertFormIsValid);
  const alertFormState = useSelector(selectAlertFormState);
  const { selectedChannels } = useSelector(selectSensorsData);

  const preEditSnapshotRef = useRef(null);

  /* INITIAL LOAD */
  useEffect(() => {
    dispatch(setAlertFormMode({ isEditing: true, alertId }));
    dispatch(fetchFilteredAlertList({ filters: [] }));
    dispatch(loadAlertForEdit({ alertId }));

    return () => {
      dispatch(resetForm());
      dispatch(clearAppliedChannelSearch());
    };
  }, [alertId, dispatch]);

  /* Capture snapshot for cancel */
  useEffect(() => {
    if (isEditLoaded && !preEditSnapshotRef.current) {
      preEditSnapshotRef.current = alertFormState;
    }
  }, [isEditLoaded, alertFormState]);

  const onCancel = () => {
    if (preEditSnapshotRef.current) {
      dispatch(restoreFormState(preEditSnapshotRef.current));
    }

    setEditingStep(null);
    setCurrentStep(AlertFormSteps.REVIEW);
  };

  const goToNextStep = () => {
    let isValid = false;

    switch (currentStep) {
      case AlertFormSteps.SELECT_SENSORS:
        isValid = dispatch(validateSelectSensorsSection());
        break;
      case AlertFormSteps.DEFINE_THRESHOLD:
        isValid = dispatch(validateDefineThresholdSection());
        break;
      case AlertFormSteps.DEFINE_ACTIONS:
        isValid = dispatch(validateDefineActionSection());
        break;
      case AlertFormSteps.NAME_ALERT:
        isValid = dispatch(validateNameAlertAndMoreSection());
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      if (editingStep) {
        setEditingStep(null);
        const incompleteSteps = getIncompleteSteps(alertFormState);
        if (incompleteSteps.length > 0) {
          const firstIncompleteStep = STEP_ORDER.find((step) => incompleteSteps.includes(step));
          if (firstIncompleteStep) {
            // navigate to the first incomplete step
            setCurrentStep(firstIncompleteStep);
            return;
          }
        }
        setCurrentStep(AlertFormSteps.REVIEW);
        return;
      }

      const currentIndex = STEP_ORDER.indexOf(currentStep);

      if (currentIndex < STEP_ORDER.length - 1) {
        setCurrentStep(STEP_ORDER[currentIndex + 1]);
      }
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);

    if (currentIndex > 0) {
      dispatch(clearStepData({ step: currentStep }));
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  };

  const onUpdate = () => {
    dispatch(updateAlert({ alertId }));
  };

  const goToStep = (step) => {
    setEditingStep(step);
    setCurrentStep(step);
  };

  /* SUMMARY RENDER */
  const renderStepSummary = (step) => {
    switch (step) {
      case AlertFormSteps.SELECT_SENSORS:
        return <SelectSensorsSummary onEdit={() => goToStep(step)} />;

      case AlertFormSteps.DEFINE_THRESHOLD:
        return <DefineThresholdSummary onEdit={() => goToStep(step)} />;

      case AlertFormSteps.DEFINE_ACTIONS:
        return <DefineActionsSummary onEdit={() => goToStep(step)} />;

      case AlertFormSteps.NAME_ALERT:
        return <NameAlertAndMoreSummary onEdit={() => goToStep(step)} />;

      default:
        return null;
    }
  };

  /* FORM RENDER */
  const renderCurrentStepForm = () => {
    switch (currentStep) {
      case AlertFormSteps.SELECT_SENSORS:
        return <SelectSensorsForm />;

      case AlertFormSteps.DEFINE_THRESHOLD:
        return <DefineThresholdsForm />;

      case AlertFormSteps.DEFINE_ACTIONS:
        return <DefineActionsForm />;

      case AlertFormSteps.NAME_ALERT:
        return <NameAlertAndMoreForm />;

      default:
        return null;
    }
  };

  const isReviewStep = currentStep === AlertFormSteps.REVIEW;

  if (!isEditLoaded) {
    return (
      <div className="row d-flex justify-content-center mt-5">
        <ProgressSpinner progressText="Loading alert…" />
      </div>
    );
  }

  if (editLoadStatus === CrudStatus.ERROR) {
    return <Alert variant="danger">Failed to load alert for editing.</Alert>;
  }

  if (isReviewStep) {
    return (
      <AlertFormReviewLayout
        onBack={goToPreviousStep}
        onCreate={onUpdate}
        onClose={() => history.push('/s/alerts')}
        goToStep={goToStep}
      />
    );
  }

  return (
    <>
      <h2>Edit Alert</h2>

      <Row className="mt-3">
        <Col>
          {currentStep === AlertFormSteps.SELECT_SENSORS && (
            <Card className="alert-card">
              <Card.Body>
                You are editing an existing alert. Changes will be applied when you save.
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Row>
        <Col>
          {STEP_ORDER.map((step, index) => {
            if (STEP_ORDER.indexOf(currentStep) > index) {
              return (
                <Row key={step} className="mt-3">
                  <Col>{renderStepSummary(step)}</Col>
                </Row>
              );
            }
            return null;
          })}
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          {renderCurrentStepForm()}
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Button variant="secondary" onClick={onCancel}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Cancel
          </Button>
        </Col>

        <Col className="text-right">
          <Button
            variant="secondary"
            onClick={goToNextStep}
            disabled={!canGoNext || selectedChannels.length === 0}
          >
            {editingStep ? 'Apply' : 'Save Changes'}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default EditAlertFormWrapper;
